/**
 * The item-shaped condition walk (D244 split, from `reference-walkers.ts`
 * at the 500-line cap): every condition that reads one ITEM's standing in
 * the world names a declared item, and a few ask for more — only a thing
 * hidden inside another is ever found (D239), and only a durable thing is
 * ever spent (D152).
 *
 * @packageDocumentation
 */

import type { ConditionDefinition } from '@orpheus/content-schema';

import { requireDeclared } from './broken-reference.js';
import { type DeclaredIdSets } from './declared-ids.js';
import { type ReferenceBrokenFailure } from './load-failure.js';

/** The conditions that read one ITEM's standing in the world. */
export type ItemCondition = Extract<ConditionDefinition, { readonly type: (typeof ITEM_CONDITION_TYPES)[number] }>;

const ITEM_CONDITION_TYPES = [
	'container_open',
	'has_item',
	'item_acquired',
	'item_equipped',
	'item_found',
	'item_in_room',
	'item_searched',
	'item_spent',
	'switched_on',
] as const satisfies readonly ConditionDefinition['type'][];

/** Whether a condition is one of the item-shaped reads. */
export const isItemCondition = (condition: ConditionDefinition): condition is ItemCondition =>
	(ITEM_CONDITION_TYPES as readonly string[]).includes(condition.type);

/**
 * Walk one item-shaped condition — the item must be declared; a few variants
 * ask for more.
 *
 * @param condition - The item condition
 * @param conditionPath - Its path, for the failure
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
export const checkItemCondition = (
	condition: ItemCondition,
	conditionPath: string,
	declared: DeclaredIdSets,
	failures: ReferenceBrokenFailure[],
): void => {
	switch (condition.type) {
		// One declared universe for every plain read: the doors authored
		// conditional text reads (D55), the hands, ever taken (D146), worn
		// (D143), gone through (D158), running (D56). Unlike `item_spent`
		// there is no dead species to catch — anything can be searched.
		case 'container_open':
		case 'has_item':
		case 'item_acquired':
		case 'item_equipped':
		case 'item_searched':
		case 'switched_on': {
			requireDeclared(declared.itemIds, condition.itemId, `${conditionPath}.itemId`, 'condition names an undeclared item', failures);
			break;
		}
		// D239: found — only a thing hidden inside another can ever be (the
		// rule `item_spent` keeps, below): a thing in plain view was never
		// hidden, so a condition asking whether it was found is dead.
		case 'item_found': {
			requireDeclared(
				declared.concealedItemIds,
				condition.itemId,
				`${conditionPath}.itemId`,
				'condition asks whether an item has been found, but the item is not concealed inside anything (`containedIn.concealed`) — it is never hidden, so it is never found',
				failures,
			);
			break;
		}
		case 'item_in_room': {
			requireDeclared(declared.itemIds, condition.itemId, `${conditionPath}.itemId`, 'condition names an undeclared item', failures);
			requireDeclared(declared.roomIds, condition.roomId, `${conditionPath}.roomId`, 'condition names an undeclared room', failures);
			break;
		}
		// D152: spent — only a thing with `durability` can ever be, so a
		// condition on any other item is constant-false and dead (D56's rule).
		case 'item_spent': {
			requireDeclared(
				declared.durableItemIds,
				condition.itemId,
				`${conditionPath}.itemId`,
				'condition asks whether an item is spent, but the item declares no durability — it can never be',
				failures,
			);
			break;
		}
		// R12: a log is declared by the item that carries it.
	}
};
